import React, { useEffect, useState } from "react";
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  CheckIcon,
  PaperAirplaneIcon,
  DocumentArrowDownIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import ApiCustom from "../../utils/ApiCustom";
import CustomTableComponent from "../../components/CustomTableComponent";
import ProductModal from "../../components/admin/ProductModal";
import ProductPreviewModal from "../../components/admin/ProductPreviewModal";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";
import ConfirmModal from "../../components/ConfirmModal";
import { formatCurrencyVND } from "../../utils/helps";

const MyProductFeature = () => {
  const companyId = localStorage.getItem("companyId");
  const [company, setCompany] = useState({});
  const [products, setProducts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [previewSlug, setPreviewSlug] = useState(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [isConfirmModal, setIsConfirmModal] = useState(false);
  const [contentModal, setContentModal] = useState("");
  const [isApproveOpen, setIsApproveOpen] = useState(false);
  const [isPendingOpen, setIsPendingOpen] = useState(false);

  const getUserPermissions = () => {
    const permissions = localStorage.getItem("permissions");
    return permissions
      ? JSON.parse(permissions).map((p) => p.permissionSlug)
      : [];
  };

  const hasPermission = (slug) => {
    const userId = localStorage.getItem("userId");
    if (userId == 1) return true;
    return getUserPermissions().includes(slug);
  };

  useEffect(() => {
    if (!companyId) return;
    ApiCustom.get(`/company-show-by-id/${companyId}`).then((res) => {
      setCompany(res.data?.data || {});
    });
    getProducts();
  }, [companyId]);

  const getProducts = async () => {
    try {
      if (companyId !== "null") {
        const res = await ApiCustom.get(`/product-show-by-user-company/`);
        setProducts(res.data?.data?.product || []);
      }
    } catch {
      setProducts([]);
    }
  };

  const sendUpdateRequest = async () => {
    try {
      if (companyId !== "null") {
        await ApiCustom.post(`/product-send-update-request/${companyId}`);
        toast.success("Gửi yêu cầu cập nhật thành công!");
        getProducts();
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Gửi yêu cầu cập nhật thất bại!",
      );
    }
  };

  const sendApproveRequest = async () => {
    try {
      await ApiCustom.post(`/product-send-approve/${companyId}`);
      toast.success("Gửi duyệt sản phẩm thành công!");
      getProducts();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Gửi duyệt sản phẩm thất bại!",
      );
    }
  };

  const exportCommitment = async () => {
    try {
      const response = await ApiCustom.get(
        `/product-export-commitment-docx/${companyId}`,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "cam-ket-chat-luong-san-pham.docx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Xuất bản cam kết thành công!");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Xuất bản cam kết thất bại!");
    }
  };

  const checkIsWebsite = () => {
    setIsModalOpen(true);
  };

  const handleOpenModal = (item = null) => {
    setCurrentProduct(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentProduct(null);
    getProducts();
  };

  const handleOpenPreviewModal = (item = null) => {
    setPreviewSlug(item?.slug || null);
    setIsPreviewOpen(true);
  };

  const handleClosePreviewModal = () => {
    setIsPreviewOpen(false);
    setPreviewSlug(null);
  };

  const handleOpenDeleteModal = (id) => {
    setDeleteId(id);
    setIsDeleteOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteId(null);
    setIsDeleteOpen(false);
    getProducts();
  };

  const handleDelete = async () => {
    try {
      await ApiCustom.delete(`/product/${deleteId}`);
      toast.success("Xóa sản phẩm thành công!");
      handleCloseDeleteModal();
    } catch {
      toast.error("Xóa sản phẩm thất bại!");
    }
  };

  const handleOpenApproveModal = (item) => {
    setCurrentProduct(item);
    setContentModal("Bạn có muốn duyệt sản phẩm này không");
    setIsApproveOpen(true);
  };

  const handleOpenPendingModal = (item) => {
    setCurrentProduct(item);
    setContentModal("Bạn có muốn bỏ duyệt sản phẩm này không");
    setIsPendingOpen(true);
  };

  const handleCloseApproveModal = () => {
    setIsApproveOpen(false);
    setCurrentProduct(null);
    setContentModal("");
    getProducts();
  };

  const handleClosePendingModal = () => {
    setIsPendingOpen(false);
    setCurrentProduct(null);
    setContentModal("");
    getProducts();
  };

  const handleApproveProduct = async () => {
    try {
      await ApiCustom.put(`/product-approve/${currentProduct?.id}`, {
        approveStatus: "approved",
      });
      toast.success("Duyệt sản phẩm thành công");
      handleCloseApproveModal();
    } catch {}
  };

  const handlePendingProduct = async () => {
    try {
      await ApiCustom.put(`/product-approve/${currentProduct?.id}`, {
        approveStatus: "pending",
      });
      toast.success("Bỏ duyệt sản phẩm thành công");
      handleClosePendingModal();
    } catch {}
  };

  const closeConfirmModal = () => {
    setIsConfirmModal(false);
    setContentModal("");
  };

  let headers = [
    {
      key: "urlImage",
      label: "Hình ảnh",
      render: (item) => (
        <div className="w-16 h-16 rounded bg-gray-50 overflow-hidden flex items-center justify-center">
          {item.urlImage ? (
            <img
              src={item.urlImage}
              alt={item.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-gray-300">No image</span>
          )}
        </div>
      ),
    },
    {
      key: "name",
      label: "Tên sản phẩm",
      render: (item) => <span className="font-medium">{item.name}</span>,
    },
    {
      key: "price",
      label: "Giá tiền",
      render: (item) => (
        <span className="text-green-700 font-semibold">
          {item?.isContactPrice === true
            ? "Liên hệ"
            : formatCurrencyVND(item.price)}
        </span>
      ),
    },
    {
      key: "status",
      label: "Trạng thái",
      render: (item) => {
        let style = "";
        let text = "";
        if (item.status === "available") {
          style = "bg-green-100 text-green-800";
          text = "Hiện có";
        } else if (item.status === "out_of_stock") {
          style = "bg-yellow-100 text-yellow-800";
          text = "Hết hàng";
        } else {
          style = "bg-red-100 text-red-800";
          text = "Ngưng cung cấp";
        }
        return (
          <span className={`px-3 py-1 rounded-full text-sm ${style}`}>
            {text}
          </span>
        );
      },
    },
    {
      key: "requestingForUpdate",
      label: "Yêu cầu cập nhật",
      render: (item) => (
        <span
          className={`px-3 py-1 rounded-full text-sm whitespace-nowrap ${
            item.requestingForUpdate
              ? "bg-blue-100 text-blue-800"
              : "bg-gray-100 text-gray-600"
          }`}
        >
          {item.requestingForUpdate
            ? "Đang gửi yêu cầu cập nhật"
            : "Chưa yêu cầu cập nhật"}
        </span>
      ),
    },
    {
      key: "approveStatus",
      label: "Trạng thái duyệt",
      render: (item) => {
        let cls = "";
        let text = "";
        if (item.approveStatus === "approved") {
          cls = "bg-green-100 text-green-800";
          text = "Đã duyệt";
        } else if (item.approveStatus === "sendingForApprove") {
          cls = "bg-blue-100 text-blue-800";
          text = "Đang gửi duyệt";
        } else {
          cls = "bg-yellow-100 text-yellow-800";
          text = "Chưa duyệt";
        }
        return (
          <span className={`px-3 py-1 rounded-full text-sm ${cls}`}>
            {text}
          </span>
        );
      },
    },
  ];

  if (hasPermission("PRODUCT_UPDATE") || hasPermission("PRODUCT_DELETE")) {
    headers.push({
      key: "setting",
      label: "Thao tác",
      render: (item) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleOpenPreviewModal(item)}
            title="Xem trước"
            className="btn btn-outline btn-primary btn-sm px-3 my-1 rounded"
          >
            <EyeIcon className="w-5 h-5" />
          </button>

          {hasPermission("PRODUCT_UPDATE") && (
            <button
              onClick={() => handleOpenModal(item)}
              title="Chỉnh sửa"
              className="btn btn-outline btn-info btn-sm px-3 my-1 rounded"
            >
              <PencilIcon className="w-5 h-5" />
            </button>
          )}
          {hasPermission("PRODUCT_DELETE") && (
            <button
              onClick={() => handleOpenDeleteModal(item.id)}
              disabled={item?.approveStatus === "approved"}
              title="Xóa"
              className="btn btn-outline btn-error btn-sm px-3 my-1 rounded"
            >
              <TrashIcon className="w-5 h-5" />
            </button>
          )}
        </div>
      ),
    });
  }

  return (
    <div className="p-6 bg-white rounded-2xl shadow-md border mb-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-3">
        <div>
          <h2 className="text-2xl font-bold text-emerald-700 mb-1">
            Sản phẩm của đơn vị bạn
          </h2>
          <p className="text-gray-500 text-sm">
            Quản lý & gửi duyệt các sản phẩm của bạn
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 max-w-2xl justify-end w-full">
          {hasPermission("PRODUCT_CREATE") && (
            <button
              onClick={() => {
                setIsModalOpen(true);
              }}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-green-700 bg-white text-green-700 font-semibold shadow-sm hover:bg-green-700 hover:text-white transition"
            >
              <PlusIcon className="w-5 h-5" />
              <span className="uppercase tracking-wide">Thêm sản phẩm</span>
            </button>
          )}
          <button
            type="button"
            onClick={sendUpdateRequest}
            className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-blue-600 bg-white text-blue-700 font-semibold shadow-sm hover:bg-blue-700 hover:text-white transition"
          >
            <PaperAirplaneIcon className="w-5 h-5" />
            <span className="uppercase tracking-wide">
              Gửi yêu cầu cập nhật
            </span>
          </button>
          <button
            type="button"
            onClick={sendApproveRequest}
            className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-green-600 bg-white text-green-700 font-semibold shadow-sm hover:bg-green-700 hover:text-white transition"
          >
            <CheckIcon className="w-5 h-5" />
            <span className="uppercase tracking-wide">Gửi duyệt sản phẩm</span>
          </button>
          <button
            type="button"
            onClick={exportCommitment}
            className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-yellow-600 bg-white text-yellow-700 font-semibold shadow-sm hover:bg-yellow-600 hover:text-white transition"
          >
            <DocumentArrowDownIcon className="w-5 h-5" />
            <span className="uppercase tracking-wide">Xuất bản cam kết</span>
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <label className="text-sm font-medium w-24">Tìm kiếm:</label>
        <input
          type="search"
          className="w-full max-w-xs px-2 py-1 border rounded-lg focus:outline-none pr-10"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
        />
      </div>

      <div className="rounded-lg shadow">
        <CustomTableComponent
          headers={headers}
          data={
            searchValue
              ? products.filter((item) =>
                  [item.name, company.companyName]
                    .filter(Boolean)
                    .some((field) =>
                      field?.toLowerCase().includes(searchValue.toLowerCase()),
                    ),
                )
              : products
          }
        />
      </div>

      {isModalOpen && (
        <ProductModal
          productSlug={currentProduct?.slug || null}
          company={company}
          onClose={handleCloseModal}
        />
      )}

      <ProductPreviewModal
        isOpen={isPreviewOpen}
        onClose={handleClosePreviewModal}
        productSlug={previewSlug}
      />

      <ConfirmDeleteModal
        isOpen={isDeleteOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDelete}
      />

      <ConfirmModal
        isOpen={isConfirmModal}
        onClose={closeConfirmModal}
        message={contentModal}
      />
      <ConfirmModal
        isOpen={isApproveOpen}
        onClose={handleCloseApproveModal}
        message={contentModal}
        onSubmit={handleApproveProduct}
      />
      <ConfirmModal
        isOpen={isPendingOpen}
        onClose={handleClosePendingModal}
        message={contentModal}
        onSubmit={handlePendingProduct}
      />
    </div>
  );
};

export default MyProductFeature;
